import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Drawer,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SmsIcon from "@mui/icons-material/Sms";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { useAuth } from "hooks/useAuth";
import { useSessions } from "hooks/useSession";
import { useClasses } from "hooks/useClasses";
import { useSessionClass } from "contexts/SessionClassContext";
import EnhancedTable from "components/common/EnhancedTable";
import {
  getWalletBalance,
  previewBroadcast,
  sendBroadcast,
  getCampaignHistory,
  getCampaignDetail,
  getHeaders,
  createStripeSession,
  verifyStripeSession,
  getMyOrders,
} from "services/broadcastService";
import axios from "axios";

interface WalletBalance {
  smsCredits: number;
  whatsappCredits: number;
  lowBalanceThreshold: number;
  smsServiceEnabled: boolean;
  whatsappServiceEnabled: boolean;
}

interface Campaign {
  id: number;
  channel: "SMS" | "WHATSAPP" | "BOTH";
  messageText: string;
  recipientScope: "ALL_STUDENTS" | "ALL_TEACHERS" | "CLASS" | "SECTION";
  totalRecipients: number;
  sent: number;
  failed: number;
  creditsHeld: number;
  creditsRefunded: number;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED";
  createdAt: string;
}

interface MessageLog {
  id: number;
  recipientType: "STUDENT" | "TEACHER";
  recipientId: number;
  phoneNumber: string;
  channel: "SMS" | "WHATSAPP";
  status: "PENDING" | "SENT" | "FAILED";
  providerMessageId: string;
  sentAt: string;
  deliveredAt: string;
}

const MessagingManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const schoolId = user?.schoolId;

  // Balance State
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(true);

  // Compose Form State
  const [channel, setChannel] = useState<"SMS" | "WHATSAPP" | "BOTH">("SMS");
  const [recipientScope, setRecipientScope] = useState<"ALL_STUDENTS" | "ALL_TEACHERS">("ALL_STUDENTS");
  const [messageText, setMessageText] = useState("");

  // Checkbox Selection & Filter State
  const [recipientsList, setRecipientsList] = useState<any[]>([]);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState<boolean>(false);

  // Preview State
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const previewTimer = useRef<any>(null);

  // History State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  // Track consecutive poll failures to stop polling when backend is unreachable
  const pollFailCountRef = useRef(0);
  const MAX_POLL_FAILURES = 3;

  // Drawer / Detail State
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [messageLogs, setMessageLogs] = useState<MessageLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Toast Msg
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Stripe Buy Credits Modal State
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [buyChannel, setBuyChannel] = useState<"SMS" | "WHATSAPP">("SMS");
  const [buyAmount, setBuyAmount] = useState<number>(1000);
  const [submittingBuy, setSubmittingBuy] = useState(false);

  // Stripe Deposit Order History State
  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
  const [ordersTotalElements, setOrdersTotalElements] = useState(0);

  // Hooks for sessions & classes
  const { sessionsQuery } = useSessions(schoolId ?? undefined);
  const { classesQuery } = useClasses(schoolId ?? undefined);
  const { selectedClass } = useSessionClass();

  const triggerToast = (msg: string, isError = false) => {
    if (isError) {
      setErrorMsg(msg);
      setSuccessMsg(null);
    } else {
      setSuccessMsg(msg);
      setErrorMsg(null);
    }
    setTimeout(() => {
      setSuccessMsg(null);
      setErrorMsg(null);
    }, 4000);
  };

  const fetchBalance = async () => {
    if (!schoolId) return;
    try {
      setLoadingBalance(true);
      const res = await getWalletBalance();
      setBalance(res.data);
      // Auto-select first enabled channel
      if (res.data) {
        if (res.data.smsServiceEnabled && !res.data.whatsappServiceEnabled) {
          setChannel("SMS");
        } else if (!res.data.smsServiceEnabled && res.data.whatsappServiceEnabled) {
          setChannel("WHATSAPP");
        }
      }
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to load wallet balance", true);
    } finally {
      setLoadingBalance(false);
    }
  };

  const fetchHistory = async (p = page, s = rowsPerPage) => {
    if (!schoolId) return;
    try {
      setLoadingHistory(true);
      const res = await getCampaignHistory(p, s);
      setCampaigns(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
      // Reset failure counter on success
      pollFailCountRef.current = 0;
    } catch (err: any) {
      pollFailCountRef.current += 1;
      // Only show toast on non-polling calls or first failure
      if (pollFailCountRef.current <= 1) {
        triggerToast(err.response?.data?.message || "Failed to load campaign history", true);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchOrders = async (p = ordersPage, s = ordersRowsPerPage) => {
    if (!schoolId) return;
    try {
      setLoadingOrders(true);
      const res = await getMyOrders(p, s);
      setOrders(res.data.content || []);
      setOrdersTotalElements(res.data.totalElements || 0);
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to load deposit history", true);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    fetchHistory();
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  // Handle Stripe Success Callback Redirects
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const payment = query.get("payment");
    const sessionId = query.get("session_id");

    if (payment === "success" && sessionId) {
      const handleVerifyPayment = async () => {
        const savedChannel = localStorage.getItem("stripe_pending_channel") || "SMS";
        const savedAmount = Number(localStorage.getItem("stripe_pending_amount") || "1000");

        try {
          triggerToast("[STRIPE] Verifying payment...");
          await verifyStripeSession(sessionId, savedChannel, savedAmount);
          triggerToast("Payment confirmed! Awaiting credit assignment by Super Admin.");
          
          // Clear query params from browser URL bar to prevent verification loop on page reload
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);

          // Clear stored channel and amount
          localStorage.removeItem("stripe_pending_channel");
          localStorage.removeItem("stripe_pending_amount");

          // Refresh balance, history, and orders
          fetchBalance();
          fetchHistory();
          fetchOrders();
        } catch (err: any) {
          triggerToast(err.response?.data?.message || "Failed to verify Stripe payment", true);
        }
      };

      handleVerifyPayment();
    } else if (payment === "cancelled") {
      triggerToast("Stripe payment was cancelled.", true);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const handleBuyOpen = (chan: "SMS" | "WHATSAPP") => {
    setBuyChannel(chan);
    setBuyAmount(500); // Default deposit amount: ₹500
    setBuyModalOpen(true);
  };

  const handleBuySubmit = async () => {
    if (buyAmount < 100) {
      triggerToast("Minimum deposit amount is ₹100", true);
      return;
    }
    try {
      setSubmittingBuy(true);
      
      // Save pending details to localStorage so we can retrieve them after Stripe redirect
      localStorage.setItem("stripe_pending_channel", buyChannel);
      localStorage.setItem("stripe_pending_amount", String(buyAmount));

      const res = await createStripeSession(buyChannel, buyAmount);
      const { sessionId, sessionUrl } = res.data;

      // Redirect user to Stripe Checkout
      window.location.href = sessionUrl;
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to initiate Stripe payment", true);
      setSubmittingBuy(false);
    }
  };

  // Auto-refresh history every 10 seconds if any campaign is PENDING or RUNNING
  useEffect(() => {
    const hasRunning = campaigns.some(c => c.status === "PENDING" || c.status === "RUNNING");
    if (!hasRunning) return;

    const interval = setInterval(async () => {
      // Stop polling if backend has been unreachable for MAX_POLL_FAILURES consecutive attempts
      if (pollFailCountRef.current >= MAX_POLL_FAILURES) {
        clearInterval(interval);
        return;
      }
      await fetchHistory(page, rowsPerPage);
      await fetchBalance(); // Refresh balance since debits/refunds may happen
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, page, rowsPerPage]);

  // Fetch recipients list reactively based on scope and global selectedClass
  useEffect(() => {
    if (!schoolId) return;

    const fetchRecipients = async () => {
      setLoadingRecipients(true);
      try {
        const headers = getHeaders();
        if (recipientScope === "ALL_STUDENTS") {
          const activeSessionId = sessionsQuery.data?.find((s: any) => s.isActive)?.id;
          let url = `${process.env.REACT_APP_API_URL}/students`;
          
          if (selectedClass) {
            const sectionStr = selectedClass.section ?? "";
            if (sectionStr === "__CLASS_ALL__") {
              url = `${process.env.REACT_APP_API_URL}/students`;
            } else if (sectionStr === "__GRADE_ALL__") {
              if (activeSessionId) {
                url = `${process.env.REACT_APP_API_URL}/students/sessionId/${activeSessionId}/className/${selectedClass.name}`;
              } else {
                url = `${process.env.REACT_APP_API_URL}/students/class/${selectedClass.name}`;
              }
            } else {
              if (activeSessionId) {
                url = `${process.env.REACT_APP_API_URL}/students/sessionId/classId?sessionId=${activeSessionId}&classId=${selectedClass.id}`;
              } else {
                url = `${process.env.REACT_APP_API_URL}/students/class/id/${selectedClass.id}`;
              }
            }
          }
          
          const response = await axios.get(url, { headers });
          const students = response.data || [];
          const formatted = students.map((s: any) => ({
            id: s.id,
            name: s.studentName || `${s.firstName || ""} ${s.lastName || ""}`.trim() || `Student #${s.id}`,
            detail: `Roll No: ${s.rollNo || s.rollNumber || "N/A"} | Class: ${s.className || "N/A"}`,
            phone: s.mobileNo || s.phone || ""
          }));
          setRecipientsList(formatted);
          setCheckedIds(formatted.map((r: any) => r.id));
        } else if (recipientScope === "ALL_TEACHERS") {
          const url = `${process.env.REACT_APP_API_URL}/staff/school/${schoolId}`;
          const response = await axios.get(url, { headers });
          const staff = response.data || [];
          const teachers = staff.filter((s: any) => s.enabled !== false);
          const formatted = teachers.map((t: any) => ({
            id: t.id,
            name: `${t.firstName || ""} ${t.lastName || ""}`.trim() || `Teacher #${t.id}`,
            detail: `Emp ID: ${t.employeeId || "N/A"} | Email: ${t.email || "N/A"}`,
            phone: t.phone || ""
          }));
          setRecipientsList(formatted);
          setCheckedIds(formatted.map((r: any) => r.id));
        } else {
          setRecipientsList([]);
          setCheckedIds([]);
        }
      } catch (err: any) {
        console.error("Failed to fetch recipients", err);
        triggerToast("Failed to fetch recipients list", true);
        setRecipientsList([]);
        setCheckedIds([]);
      } finally {
        setLoadingRecipients(false);
      }
    };

    fetchRecipients();
  }, [recipientScope, selectedClass, schoolId, sessionsQuery.data]);

  // Run Preview with Debounce
  const handlePreview = async () => {
    // Validate inputs
    if (checkedIds.length === 0) {
      setPreviewData(null);
      return;
    }

    try {
      setPreviewLoading(true);
      const payload = {
        channel,
        messageText: messageText || "Preview Message",
        recipientScope,
        classId: null,
        sectionId: null,
        recipientIds: checkedIds,
      };
      const res = await previewBroadcast(payload);
      setPreviewData(res.data);
    } catch (err) {
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (previewTimer.current) {
      clearTimeout(previewTimer.current);
    }
    if (!balance || (!balance.smsServiceEnabled && !balance.whatsappServiceEnabled)) return;

    previewTimer.current = setTimeout(() => {
      handlePreview();
    }, 400); // 400ms debounce

    return () => clearTimeout(previewTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, recipientScope, checkedIds, messageText, balance]);

  const handleSend = async () => {
    // Basic validations
    if (messageText.trim() === "") {
      triggerToast("Message text cannot be empty", true);
      return;
    }
    if (checkedIds.length === 0) {
      triggerToast("Please select at least one recipient", true);
      return;
    }
    if (previewData && !previewData.sufficientBalance) {
      triggerToast("Insufficient wallet balance to send", true);
      return;
    }

    try {
      const payload = {
        channel,
        messageText,
        recipientScope,
        classId: null,
        sectionId: null,
        recipientIds: checkedIds,
      };
      const res = await sendBroadcast(payload);
      triggerToast(`Campaign launched successfully! Campaign ID: ${res.data}`);
      // Reset form
      setMessageText("");
      setPreviewData(null);
      // Refresh balance and history
      fetchBalance();
      fetchHistory(0, rowsPerPage);
      setPage(0);
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to send broadcast", true);
    }
  };

  const handleRowClick = async (row: Campaign) => {
    setSelectedCampaign(row);
    setDrawerOpen(true);
    try {
      setLoadingLogs(true);
      const res = await getCampaignDetail(row.id);
      setMessageLogs(res.data.logs || []);
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to load campaign logs", true);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Compose form visibility
  const isMessagingEnabled = balance && (balance.smsServiceEnabled || balance.whatsappServiceEnabled);

  // Column definitions for Campaign History Table
  const historyColumns = [
    {
      id: "createdAt",
      label: t("date", "Date"),
      format: (val: any) => (val ? new Date(val).toLocaleString() : ""),
    },
    { id: "channel", label: t("channel", "Channel") },
    { id: "recipientScope", label: t("scope", "Scope") },
    {
      id: "totalRecipients",
      label: t("recipients", "Recipients"),
      align: "center" as const,
      format: (val: any) => val.toLocaleString(),
    },
    {
      id: "sent",
      label: t("sent", "Sent"),
      align: "center" as const,
      format: (val: any) => val.toLocaleString(),
    },
    {
      id: "failed",
      label: t("failed", "Failed"),
      align: "center" as const,
      format: (val: any) => val.toLocaleString(),
    },
    {
      id: "creditsHeld",
      label: t("credits_used", "Credits Used"),
      align: "center" as const,
      format: (val: any, row: any) => (row.creditsHeld - row.creditsRefunded).toLocaleString(),
    },
    {
      id: "status",
      label: t("status", "Status"),
      align: "center" as const,
      format: (val: any) => {
        let color: "default" | "primary" | "success" | "error" = "default";
        if (val === "RUNNING") color = "primary";
        if (val === "COMPLETED") color = "success";
        if (val === "FAILED") color = "error";

        return (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Chip
              label={val}
              size="small"
              color={color}
              sx={{ fontWeight: 700, borderRadius: 1 }}
            />
            {val === "RUNNING" && <CircularProgress size={14} color="primary" />}
          </Stack>
        );
      },
    },
  ];

  const orderColumns = [
    {
      id: "createdAt",
      label: t("date", "Date"),
      format: (val: any) => (val ? new Date(val).toLocaleString() : ""),
    },
    { id: "channel", label: t("channel", "Channel") },
    {
      id: "amountPaidRupees",
      label: t("amount_paid", "Amount Paid"),
      format: (val: any) => `₹${val.toFixed(2)}`,
    },
    {
      id: "status",
      label: t("status", "Status"),
      align: "center" as const,
      format: (val: any) => {
        let color: "default" | "primary" | "success" | "error" | "warning" = "default";
        if (val === "PENDING") color = "warning";
        if (val === "PAID_PENDING_ASSIGNMENT") color = "primary";
        if (val === "ASSIGNED") color = "success";
        if (val === "REJECTED") color = "error";
        return <Chip label={val.replace(/_/g, " ")} size="small" color={color} sx={{ fontWeight: 700 }} />;
      },
    },
    {
      id: "assignedCreditAmount",
      label: t("assigned_credits", "Assigned Credits"),
      align: "center" as const,
      format: (val: any) => (val != null ? val.toLocaleString() : "-"),
    },
    {
      id: "assignedAt",
      label: t("assigned_date", "Assigned At"),
      format: (val: any) => (val ? new Date(val).toLocaleString() : "-"),
    },
    {
      id: "rejectionReason",
      label: t("rejection_reason", "Rejection Reason"),
      format: (val: any) => val || "-",
    },
  ];

  // Log table columns in details drawer
  const logColumns = [
    { id: "recipientType", label: "Recipient Type" },
    { id: "phoneNumber", label: "Phone Number" },
    { id: "channel", label: "Channel" },
    {
      id: "status",
      label: "Status",
      format: (val: any) => {
        let color: "default" | "success" | "error" = "default";
        if (val === "SENT") color = "success";
        if (val === "FAILED") color = "error";
        return <Chip label={val} size="small" color={color} sx={{ fontWeight: 600 }} />;
      },
    },
    { id: "providerMessageId", label: "Provider Message ID" },
    {
      id: "sentAt",
      label: "Sent At",
      format: (val: any) => (val ? new Date(val).toLocaleTimeString() : ""),
    },
    {
      id: "deliveredAt",
      label: "Delivered At",
      format: (val: any) => (val ? new Date(val).toLocaleTimeString() : ""),
    },
  ];

  return (
    <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Success / Error Alerts */}
      {successMsg && <Alert severity="success">{successMsg}</Alert>}
      {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

      {/* Page Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <SmsIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {t("messaging", "Broadcast Messaging")}
        </Typography>
      </Box>

      {/* SECTION A — CREDIT BALANCE WIDGETS */}
      <Grid container spacing={3}>
        {loadingBalance ? (
          <Grid item xs={12}>
            <CircularProgress />
          </Grid>
        ) : !isMessagingEnabled ? (
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ width: "100%", borderRadius: 2 }}>
              Messaging is not enabled for this school. Please contact your administrator to enable SMS or WhatsApp services.
            </Alert>
          </Grid>
        ) : (
          <>
            {/* SMS CARD */}
            {balance?.smsServiceEnabled && (
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #1f4068 0%, #162447 100%)",
                    color: "#fff",
                    borderRadius: 3,
                    boxShadow: 3,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                      SMS Credits (MSG91)
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mt: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 800 }}>
                        {balance.smsCredits.toLocaleString()}
                      </Typography>
                      {balance.smsCredits < balance.lowBalanceThreshold && (
                        <Chip label="Low Balance" size="small" color="warning" sx={{ fontWeight: 700 }} />
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => handleBuyOpen("SMS")}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                        textTransform: "none",
                        borderRadius: 2,
                        mt: 2,
                      }}
                    >
                      + Buy SMS Credits
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* WHATSAPP CARD */}
            {balance?.whatsappServiceEnabled && (
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                    color: "#fff",
                    borderRadius: 3,
                    boxShadow: 3,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                      WhatsApp Credits (Wassenger)
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mt: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 800 }}>
                        {balance.whatsappCredits.toLocaleString()}
                      </Typography>
                      {balance.whatsappCredits < balance.lowBalanceThreshold && (
                        <Chip label="Low Balance" size="small" color="warning" sx={{ fontWeight: 700 }} />
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => handleBuyOpen("WHATSAPP")}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                        textTransform: "none",
                        borderRadius: 2,
                        mt: 2,
                      }}
                    >
                      + Buy WhatsApp Credits
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </>
        )}
      </Grid>

      {/* SECTION B — COMPOSE FORM */}
      {isMessagingEnabled && (
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Compose Broadcast
            </Typography>

            <Grid container spacing={3}>
              {/* Channel Selector */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Channel
                </Typography>
                <ToggleButtonGroup
                  color="primary"
                  value={channel}
                  exclusive
                  onChange={(e, val) => val && setChannel(val)}
                  fullWidth
                  size="small"
                >
                  {balance?.smsServiceEnabled && <ToggleButton value="SMS">SMS</ToggleButton>}
                  {balance?.whatsappServiceEnabled && <ToggleButton value="WHATSAPP">WhatsApp</ToggleButton>}
                  {balance?.smsServiceEnabled && balance?.whatsappServiceEnabled && (
                    <ToggleButton value="BOTH">Both</ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Grid>

              {/* Recipient Scope */}
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Recipient Scope
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={recipientScope}
                    onChange={(e) => {
                      setRecipientScope(e.target.value as any);
                    }}
                  >
                    <MenuItem value="ALL_STUDENTS">All Students</MenuItem>
                    <MenuItem value="ALL_TEACHERS">All Teachers</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Checked Recipients List Checklist */}
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Select Recipients ({checkedIds.length} of {recipientsList.length} selected)
                </Typography>
                {recipientsList.length > 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={recipientsList.length > 0 && checkedIds.length === recipientsList.length}
                        indeterminate={checkedIds.length > 0 && checkedIds.length < recipientsList.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCheckedIds(recipientsList.map(r => r.id));
                          } else {
                            setCheckedIds([]);
                          }
                        }}
                        color="primary"
                      />
                    }
                    label="Select All"
                    sx={{ mr: 0 }}
                  />
                )}
              </Stack>
              
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 250, overflowY: "auto", bgcolor: "#fff", borderRadius: 2 }}>
                {loadingRecipients ? (
                  <Box sx={{ p: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : recipientsList.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No recipients found.
                    </Typography>
                  </Box>
                ) : (
                  <List dense sx={{ width: "100%", bgcolor: "background.paper" }}>
                    {recipientsList.map((recipient) => {
                      const isChecked = checkedIds.includes(recipient.id);
                      return (
                        <ListItem
                          key={recipient.id}
                          secondaryAction={
                            <Checkbox
                              edge="end"
                              onChange={() => {
                                setCheckedIds(prev => 
                                  prev.includes(recipient.id) 
                                    ? prev.filter(x => x !== recipient.id) 
                                    : [...prev, recipient.id]
                                );
                              }}
                              checked={isChecked}
                            />
                          }
                          disablePadding
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            '&:hover': {
                              bgcolor: "#f5f5f5"
                            }
                          }}
                        >
                          <ListItemButton onClick={() => {
                            setCheckedIds(prev => 
                              prev.includes(recipient.id) 
                                ? prev.filter(x => x !== recipient.id) 
                                : [...prev, recipient.id]
                            );
                          }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: isChecked ? "primary.main" : "grey.400", width: 32, height: 32, fontSize: 14 }}>
                                {recipient.name.split(" ").map((n: string) => n[0]).join("").substring(0, 2).toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={recipient.name}
                              secondary={recipient.detail}
                              primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                              secondaryTypographyProps={{ fontSize: "0.75rem" }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Box>

            {/* Message Body */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Message Text
              </Typography>
              <TextField
                multiline
                rows={4}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                inputProps={{
                  maxLength: channel === "SMS" ? 160 : 1024,
                }}
                fullWidth
                placeholder={
                  channel === "SMS"
                    ? "Type your SMS broadcast..."
                    : "Type your WhatsApp broadcast message. Make sure it matches your Wassenger template..."
                }
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                {channel === "SMS" ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: messageText.length > 160 ? "warning.main" : "text.secondary",
                      fontWeight: messageText.length > 160 ? 700 : 400,
                    }}
                  >
                    {messageText.length} / 160 characters (longer messages will be split into multiple SMS)
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Ensure message matches your approved template (Max 1024 chars)
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Preview Bar */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa", borderRadius: 2 }}>
              {previewLoading ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Generating campaign estimation...
                  </Typography>
                </Stack>
              ) : previewData ? (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {previewData.sufficientBalance ? (
                      <CheckCircleOutlineIcon color="success" />
                    ) : (
                      <ErrorOutlineIcon color="error" />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      This will send to {previewData.recipientCount} recipients ({previewData.uniquePhones} unique phones).
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Estimated cost:{" "}
                    <strong>
                      {previewData.estimatedSmsCredits} SMS + {previewData.estimatedWhatsappCredits} WhatsApp credits
                    </strong>
                    .
                  </Typography>
                  {!previewData.sufficientBalance && (
                    <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
                      Insufficient wallet balance to cover the estimated credit costs. Please contact your platform administrator.
                    </Alert>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  Fill out the form to calculate recipient details and credit cost estimations.
                </Typography>
              )}
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSend}
                disabled={
                  previewLoading ||
                  (previewData && !previewData.sufficientBalance) ||
                  messageText.trim() === "" ||
                  checkedIds.length === 0
                }
                sx={{ borderRadius: 2, px: 4 }}
              >
                Send Broadcast
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* SECTION C — CAMPAIGN HISTORY */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Broadcast History
          </Typography>
          <IconButton onClick={() => fetchHistory(page, rowsPerPage)} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>

        <EnhancedTable
          columns={historyColumns}
          data={campaigns}
          loading={loadingHistory}
          enablePagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={totalElements}
          onPageChange={(e, newPage) => {
            setPage(newPage);
            fetchHistory(newPage, rowsPerPage);
          }}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            setRowsPerPage(newSize);
            setPage(0);
            fetchHistory(0, newSize);
          }}
          onRowClick={handleRowClick}
          cardProps={{
            elevation: 3,
            sx: { borderRadius: 3 },
          }}
        />
      </Box>

      {/* SECTION D — STRIPE DEPOSIT HISTORY */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Stripe Deposit History
          </Typography>
          <IconButton onClick={() => fetchOrders(ordersPage, ordersRowsPerPage)} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>

        <EnhancedTable
          columns={orderColumns}
          data={orders}
          loading={loadingOrders}
          enablePagination={true}
          page={ordersPage}
          rowsPerPage={ordersRowsPerPage}
          totalElements={ordersTotalElements}
          onPageChange={(e, newPage) => {
            setOrdersPage(newPage);
            fetchOrders(newPage, ordersRowsPerPage);
          }}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            setOrdersRowsPerPage(newSize);
            setOrdersPage(0);
            fetchOrders(0, newSize);
          }}
          cardProps={{
            elevation: 3,
            sx: { borderRadius: 3 },
          }}
        />
      </Box>

      {/* DETAILS DRAWER */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: "100vw", md: 700 }, p: 4, display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Drawer Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Campaign Details
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedCampaign && (
            <Card variant="outlined" sx={{ bgcolor: "#fafafa", borderRadius: 2 }}>
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Message Content:
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: "italic", p: 1.5, bgcolor: "#fff", border: "1px dashed #ccc", borderRadius: 1 }}>
                  {selectedCampaign.messageText}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Channel</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCampaign.channel}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Scope</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCampaign.recipientScope}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Sent / Failed</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCampaign.sent} / {selectedCampaign.failed}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Credits Spent</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedCampaign.creditsHeld - selectedCampaign.creditsRefunded}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Delivery logs */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              Delivery Logs
            </Typography>
            <EnhancedTable
              columns={logColumns}
              data={messageLogs}
              loading={loadingLogs}
              cardProps={{
                elevation: 0,
                sx: { border: "1px solid #e0e0e0", borderRadius: 2 },
              }}
            />
          </Box>
        </Box>
      </Drawer>

      {/* BUY CREDITS MODAL */}
      <Dialog open={buyModalOpen} onClose={() => !submittingBuy && setBuyModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Deposit funds for {buyChannel} Credits</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Specify the amount in Rupees to pay via Stripe Checkout. An administrator will manually assign credits once payment is confirmed.
          </Typography>
          <TextField
            label="Amount (INR)"
            type="number"
            value={buyAmount}
            onChange={(e) => setBuyAmount(Math.max(100, Number(e.target.value)))}
            fullWidth
            size="small"
            inputProps={{ min: 100 }}
            InputProps={{
              startAdornment: (
                <Typography variant="body2" sx={{ mr: 1, color: "text.secondary" }}>
                  ₹
                </Typography>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Minimum deposit: ₹100.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBuyModalOpen(false)} disabled={submittingBuy} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleBuySubmit} variant="contained" disabled={submittingBuy || buyAmount < 100} startIcon={submittingBuy ? <CircularProgress size={20} /> : <SendIcon />}>
            {submittingBuy ? "Redirecting..." : `Pay ₹${buyAmount}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessagingManagement;
